<template>
  <div class="card-body pt-0 edge-to-edge" v-if="$gate.isAdmin()">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h5 class="mb-0">Registru Jurnal</h5>
      <div class="d-flex gap-2">
        <button type="button" class="btn btn-sm btn-primary" @click="openNotaContabilaModal">
          <i class="fa fa-plus me-1"></i> Notă Contabilă
        </button>
        <button type="button" class="btn btn-sm btn-light-primary" @click="reload">
          <i class="fa fa-rotate-right me-1"></i>
          Reîncarcă
        </button>
      </div>
    </div>

    <!-- Filtre Registru Jurnal -->
    <div class="mb-5">
      <form @submit.prevent="applyFilters">
        <div class="row g-3 align-items-end">
          <div class="col-md-3">
            <label class="form-label">Perioadă de la</label>
            <input type="date" class="form-control" v-model="filters.data_start" />
          </div>
          <div class="col-md-3">
            <label class="form-label">Perioadă până la</label>
            <input type="date" class="form-control" v-model="filters.data_end" />
          </div>
          <div class="col-md-3">
            <label class="form-label">Tip document</label>
            <label class="form-label">Tip document</label>
            <multiselect v-model="filters.tip_document" :options="tipuriDocument" track-by="id" label="descriere"
              placeholder="Selectează tip" :searchable="true" :allow-empty="true" class="w-100"></multiselect>
          </div>
          <div class="col-md-3">
            <label class="form-label">Document sursă</label>
            <input type="text" class="form-control" placeholder="Caută în descriere / sursă"
              v-model="filters.document_sursa" />
          </div>

          <div class="col-md-3">
            <label class="form-label">Cont (D/C)</label>
            <input type="text" class="form-control" placeholder="ex: 5121, 411.01..." v-model="filters.cont" />
          </div>
          <div class="col-md-2">
            <label class="form-label">Sumă minimă</label>
            <input type="number" step="0.01" class="form-control" v-model.number="filters.suma_min" />
          </div>
          <div class="col-md-2">
            <label class="form-label">Sumă maximă</label>
            <input type="number" step="0.01" class="form-control" v-model.number="filters.suma_max" />
          </div>
          <div class="col-md-3">
            <label class="form-label">Căutare generală</label>
            <input type="text" class="form-control" placeholder="Explicație, număr document..."
              v-model="filters.search" />
          </div>

          <div class="col-md-2 text-end">
            <button type="button" class="btn btn-primary me-2" @click="applyFilters">
              <i class="fa fa-search me-1"></i>
              Aplică filtre
            </button>
            <button type="button" class="btn btn-light" @click="resetFilters">
              Reset
            </button>
          </div>
        </div>
      </form>
    </div>

    <!-- Tabel Registru Jurnal -->
    <!-- Mesaje stare tabel -->
    <div v-if="loading" class="text-center py-4">
      Se încarcă înregistrările din Registru Jurnal...
    </div>
    <div v-else-if="!linii.length" class="text-center text-muted py-4">
      Nu există înregistrări în Registru Jurnal pentru această firmă și filtru.
    </div>
    <div class="table-responsive">
      <table class="table table-bordered table-striped align-middle" id="tabel_registru_jurnal">
        <thead>
          <tr class="text-uppercase fw-bold">
            <th style="width: 40px" class="text-center">#</th>
            <th style="width: 110px;">Data doc</th>
            <th style="width: 110px;">Data înreg.</th>
            <th style="width: 120px;">Tip document</th>
            <th style="width: 150px;">Document</th>
            <th>Document sursă</th>
            <th>Explicație</th>
            <th style="width: 110px;">Cont D</th>
            <th style="width: 110px;">Cont C</th>
            <th style="width: 120px;" class="text-end">Sumă</th>
            <th style="width: 110px;" class="text-center">Acțiuni</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(linie, index) in linii" :key="linie.linie_id">
            <td class="text-center">{{ index + 1 }}</td>
            <td>{{ formatDate(linie.doc_data) || '-' }}</td>
            <td>{{ formatDate(linie.data_inregistrare) || '-' }}</td>
            <td>{{ linie.tip_document || '-' }}</td>
            <td class="clickable-cell" @click="showDetalii(linie)">
              <div>
                <strong class="text-primary">
                  {{ (linie.doc_serie || '') + (linie.doc_numar || '-') }}
                </strong>
              </div>
            </td>
            <td class="clickable-cell" @click="showDetalii(linie)">{{ linie.doc_descriere || '-' }}</td>
            <td class="clickable-cell" @click="showDetalii(linie)">{{ linie.explicatie || '-' }}</td>
            <td>
              <div>{{ linie.cont_debit_cod || '-' }}</div>
              <small class="text-muted" v-if="linie.cont_debit_descriere">
                {{ linie.cont_debit_descriere }}
              </small>
            </td>
            <td>
              <div>{{ linie.cont_credit_cod || '-' }}</div>
              <small class="text-muted" v-if="linie.cont_credit_descriere">
                {{ linie.cont_credit_descriere }}
              </small>
            </td>
            <td class="text-end">
              {{ formatMoney(linie.suma_ron) }}
            </td>
            <td class="text-center">
              <div class="d-flex flex-column gap-1">
                <button type="button" class="btn btn-sm btn-light-primary" @click="setEditingLinie(linie)">
                  <i class="fa fa-pen-to-square me-1"></i>
                  Editare
                </button>
                <button type="button" class="btn btn-sm btn-light-danger" @click="confirmDelete(linie)">
                  <i class="fa fa-trash me-1"></i>
                  Șterge
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Modal editare linie Registru Jurnal -->
    <div class="modal fade" id="modalEditLinieRJ" tabindex="-1" aria-labelledby="modalEditLinieRJLabel"
      aria-hidden="true">
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="modalEditLinieRJLabel">
              Editare linie Registru Jurnal
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div v-if="!editingLinie" class="text-muted">
              Nu este selectată nicio linie pentru editare.
            </div>
            <div v-else>
              <div class="mb-3">
                <small class="text-muted">ID linie:</small>
                <div><strong>{{ editingLinie.linie_id }}</strong></div>
              </div>

              <form @submit.prevent="saveEditingLinie">
                <div class="row g-3">
                  <div class="col-md-4">
                    <label class="form-label">Data înregistrare</label>
                    <input type="date" class="form-control" v-model="editingLinie.data_inregistrare" />
                  </div>

                  <div class="col-md-4">
                    <label class="form-label">Sumă</label>
                    <input type="number" step="0.01" class="form-control" v-model.number="editingLinie.suma_ron" />
                  </div>

                  <div class="col-md-4">
                    <label class="form-label">Monedă</label>
                    <input type="text" class="form-control" v-model="editingLinie.moneda" disabled />
                  </div>

                  <div class="col-md-6">
                    <label class="form-label">Cont D</label>
                    <div class="input-group">
                      <input type="text" class="form-control" placeholder="Cont D"
                        v-model="editingLinie.cont_debit_baza" />
                      <span class="input-group-text">.</span>
                      <input type="text" class="form-control" placeholder="Analitic (opțional)"
                        v-model="editingLinie.cont_debit_analitic" />
                    </div>
                  </div>

                  <div class="col-md-6">
                    <label class="form-label">Cont C</label>
                    <div class="input-group">
                      <input type="text" class="form-control" placeholder="Cont C"
                        v-model="editingLinie.cont_credit_baza" />
                      <span class="input-group-text">.</span>
                      <input type="text" class="form-control" placeholder="Analitic (opțional)"
                        v-model="editingLinie.cont_credit_analitic" />
                    </div>
                  </div>

                  <div class="col-12">
                    <label class="form-label">Explicație</label>
                    <textarea class="form-control" rows="3" v-model="editingLinie.explicatie"></textarea>
                  </div>
                </div>
              </form>
            </div>
          </div>
          <div class="modal-footer flex-column align-items-stretch align-items-md-center">
            <div class="d-flex justify-content-end w-100">
              <button type="button" class="btn btn-light me-2" data-bs-dismiss="modal">
                Închide
              </button>
              <button type="button" class="btn btn-primary" :disabled="savingEdit || !editingLinie"
                @click="saveEditingLinie">
                <span v-if="savingEdit" class="spinner-border spinner-border-sm me-2" role="status"
                  aria-hidden="true"></span>
                Salvează modificările
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Detalii Document (RJ Complet + Plati) -->
    <div class="modal fade" id="modalDetaliiDocument" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Detalii Document: {{ (detaliiDoc.antet?.doc_serie || '') +
              (detaliiDoc.antet?.doc_numar || '') }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body" v-if="detaliiLoading">
            <div class="text-center py-5">
              <div class="spinner-border text-primary" role="status"></div>
              <div class="mt-2">Se încarcă detaliile documentului...</div>
            </div>
          </div>
          <div class="modal-body" v-else-if="detaliiDoc.antet">
            <!-- Informatii Antet -->
            <div class="row mb-4">
              <div class="col-md-6">
                <div class="text-muted small">Tip Document</div>
                <div class="fw-bold">{{ detaliiDoc.antet.tip_document }}</div>
              </div>
              <div class="col-md-6 text-end">
                <div class="text-muted small">Data Document</div>
                <div class="fw-bold">{{ formatDate(detaliiDoc.antet.doc_data) }}</div>
              </div>
            </div>

            <!-- Linii Contabile -->
            <div class="mb-4">
              <h6 class="fw-bold mb-3"><i class="fa fa-list-ul me-2"></i>Linii Contabile (RJ)</h6>
              <div class="table-responsive">
                <table class="table table-sm table-bordered bg-light">
                  <thead class="table-light">
                    <tr>
                      <th>Debit</th>
                      <th>Credit</th>
                      <th class="text-end">Sumă</th>
                      <th>Explicație</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="l in detaliiDoc.linii" :key="l.id">
                      <td>{{ l.cont_debit_cod }}</td>
                      <td>{{ l.cont_credit_cod }}</td>
                      <td class="text-end">{{ formatMoney(l.suma_ron) }}</td>
                      <td>{{ l.descriere }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Documente Repartizate (Facturi pt Plati sau Plati pt Facturi) -->
            <div v-if="detaliiDoc.repartizari && detaliiDoc.repartizari.length > 0">
              <h6 class="fw-bold mb-3">
                <i class="fa fa-money-bill-transfer me-2"></i>
                <span v-if="isInvoice(detaliiDoc.antet.tip_document)">Istoric Încasări / Plăți</span>
                <span v-else>Documente Repartizate</span>
              </h6>
              <div class="table-responsive">
                <table class="table table-sm table-bordered border-success">
                  <thead class="bg-light-success">
                    <tr>
                      <th>Data</th>
                      <th>Document</th>
                      <th>Tip</th>
                      <th class="text-end">Sumă</th>
                      <th>Detalii Contabile (D / C)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(r, idx) in detaliiDoc.repartizari" :key="idx">
                      <td>{{ formatDate(r.data) }}</td>
                      <td><strong>{{ r.numar }}</strong></td>
                      <td><small class="text-muted">{{ r.tip }}</small></td>
                      <td class="text-end fw-bold text-success">{{ formatMoney(r.suma) }}</td>
                      <td>
                        <div v-for="(l, lIdx) in r.rj_linii" :key="lIdx"
                          class="small border-bottom border-light pb-1 mb-1">
                          <span class="badge bg-light text-primary border border-primary-subtle">{{
                            l.cont_debit_cod_complet }}</span>
                          <i class="fa fa-arrow-right mx-1 text-muted"></i>
                          <span class="badge bg-light text-success border border-success-subtle">{{
                            l.cont_credit_cod_complet }}</span>
                          <div class="text-muted" style="font-size: 0.75rem;">{{ l.descriere }}</div>
                        </div>
                        <div v-if="!r.rj_linii || r.rj_linii.length === 0" class="text-muted small">
                          Fără înregistrări RJ găsite.
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div v-else-if="shouldShowRepartizariAlert(detaliiDoc.antet.tip_document)"
              class="alert alert-light-warning py-2 border-0">
              <i class="fa fa-info-circle me-2"></i> Nu există documente repartizate corelate (repartizarea nu a fost
              efectuată).
            </div>

          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">Închide</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Nota Contabila -->
    <NotaContabilaModal ref="notaModal" :firma-id="firmaId" @saved="loadRegistru" />

  </div>
</template>

<script>
import Form from 'vform'
import NotaContabilaModal from '../../Contabilitate/NotaContabilaModal.vue'
import axios from 'axios'
import Swal from 'sweetalert2'
import Multiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.css'

export default {
  name: 'RegistruJurnal',
  components: {
    Multiselect,
    NotaContabilaModal
  },
  props: {
    firmaId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      filters: new Form({
        data_start: '',
        data_end: '',
        tip_document: null,
        document_sursa: '',
        cont: '',
        suma_min: '',
        suma_max: '',
        search: '',
      }),
      tipuriDocument: [],
      linii: [],
      loading: false,
      dtRegistru: null,
      editingLinie: null,
      savingEdit: false,

      // Document details modal
      detaliiDoc: {
        antet: null,
        linii: [],
        repartizari: []
      },
      detaliiLoading: false,
    }
  },
  watch: {
    firmaId: {
      immediate: true,
      handler() {
        this.fetchTipuriDocumente()
        this.loadRegistru()
      },
    },
  },
  methods: {
    formatDate(d) {
      if (!d) return ''
      try {
        return new Date(d).toLocaleDateString('ro-RO')
      } catch (e) {
        return ''
      }
    },
    formatMoney(v) {
      const n = Number(v || 0)
      return n.toLocaleString('ro-RO', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },
    initDataTable() {
      if (this.dtRegistru && this.dtRegistru.destroy) {
        this.dtRegistru.destroy()
        this.dtRegistru = null
      }
      // Dacă nu avem linii, nu inițializăm DataTables
      if (!this.linii || this.linii.length === 0) {
        return
      }

      if (!window.$ || !window.$.fn || !window.$.fn.DataTable) {
        return
      }

      const $table = window.$('#tabel_registru_jurnal')
      if (!$table.length) return

      this.dtRegistru = $table.DataTable({
        paging: true,
        searching: true,
        info: true,
        ordering: true,
        pageLength: 25,
        order: [[0, 'asc']],
        language: {
          url: 'https://cdn.datatables.net/plug-ins/1.13.6/i18n/ro.json',
          search: 'Caută:',
          lengthMenu: 'Afișează _MENU_ înregistrări',
          info: 'Afișate _START_ - _END_ din _TOTAL_ înregistrări',
          infoEmpty: 'Nu sunt date',
          zeroRecords: 'Nu am găsit nimic',
          paginate: {
            first: 'Prima',
            last: 'Ultima',
            next: '›',
            previous: '‹',
          },
        },
      })
    },
    async loadRegistru() {
      if (!this.firmaId) return
      this.loading = true

      // distrugem instanța DataTables înainte de re-render
      if (this.dtRegistru && this.dtRegistru.destroy) {
        this.dtRegistru.destroy()
        this.dtRegistru = null
      }

      try {
        const params = {
          firma_id: this.firmaId,
          data_start: this.filters.data_start || null,
          data_end: this.filters.data_end || null,
          tip_document: this.filters.tip_document ? (this.filters.tip_document.id || this.filters.tip_document.descriere || this.filters.tip_document) : null,
          document_sursa: this.filters.document_sursa || null,
          cont: this.filters.cont || null,
          suma_min: this.filters.suma_min || null,
          suma_max: this.filters.suma_max || null,
          search: this.filters.search || null,
          per_page: 5000,
        }

        const res = await axios.get('/api/administrare/work/rj/list', {
          params,
        })

        const data = res.data
        this.linii = Array.isArray(data)
          ? data
          : data.items || []

        this.$nextTick(() => {
          this.initDataTable()
        })
      } catch (e) {
        console.error('Eroare la încărcarea Registrului Jurnal:', e)
        this.linii = []
        const msg =
          e?.response?.data?.error ||
          'Nu s-au putut încărca înregistrările din Registru Jurnal.'
        if (window.Swal) {
          window.Swal.fire({
            icon: 'error',
            title: 'Eroare',
            text: msg,
          })
        } else if (window.toastr) {
          window.toastr.error(msg)
        }
      }

      this.loading = false
    },
    applyFilters() {
      this.loadRegistru()
    },
    resetFilters() {
      if (this.filters && typeof this.filters.reset === 'function') {
        this.filters.reset()
      } else {
        this.filters = new Form({
          data_start: '',
          data_end: '',
          tip_document: null,
          document_sursa: '',
          cont: '',
          suma_min: '',
          suma_max: '',
          search: '',
        })
      }
      this.loadRegistru()
    },
    reload() {
      this.loadRegistru()
    },
    setEditingLinie(linie) {
      if (!linie) return

      // Dacă este Notă Contabilă, folosim modalul unificat (full NC)
      const tipDoc = (linie.tip_document || '').toLowerCase();
      const isNC = tipDoc.includes('nota') || tipDoc.includes('nc') || tipDoc.includes('contabil');
      
      if (isNC) {
        if (this.$refs.notaModal) {
          this.$refs.notaModal.editNota(linie.reg_jurnal_id);
        } else {
          console.error('NotaContabilaModal ref not found');
        }
        return;
      }

      const copie = { ...linie }

      // Spargem conturile în bază + analitic, după punct
      if (copie.cont_debit_cod) {
        const partsD = String(copie.cont_debit_cod).split('.')
        copie.cont_debit_baza = partsD[0] || ''
        copie.cont_debit_analitic = partsD[1] || ''
      } else {
        copie.cont_debit_baza = ''
        copie.cont_debit_analitic = ''
      }

      if (copie.cont_credit_cod) {
        const partsC = String(copie.cont_credit_cod).split('.')
        copie.cont_credit_baza = partsC[0] || ''
        copie.cont_credit_analitic = partsC[1] || ''
      } else {
        copie.cont_credit_baza = ''
        copie.cont_credit_analitic = ''
      }

      this.editingLinie = copie

      // Deschidem manual modalul pentru editare linie (pentru alte tipuri de doc)
      try {
        const $m = window.$('#modalEditLinieRJ')
        if ($m.length) {
          const bootstrapModal = new window.bootstrap.Modal($m[0])
          bootstrapModal.show()
        }
      } catch (e) {
        console.warn('Bootstrap modal init error (edit-linie)', e)
      }
    },
    async saveEditingLinie() {
      if (!this.editingLinie || !this.firmaId) return

      this.savingEdit = true

      try {
        const contDebitBaza = (this.editingLinie.cont_debit_baza || '').toString().trim()
        const contDebitAnalitic = (this.editingLinie.cont_debit_analitic || '').toString().trim()
        const contCreditBaza = (this.editingLinie.cont_credit_baza || '').toString().trim()
        const contCreditAnalitic = (this.editingLinie.cont_credit_analitic || '').toString().trim()

        const cont_debit_full =
          contDebitBaza !== ''
            ? (contDebitAnalitic !== ''
              ? `${contDebitBaza}.${contDebitAnalitic}`
              : contDebitBaza)
            : null

        const cont_credit_full =
          contCreditBaza !== ''
            ? (contCreditAnalitic !== ''
              ? `${contCreditBaza}.${contCreditAnalitic}`
              : contCreditBaza)
            : null

        const payload = {
          firma_id: this.firmaId,
          linie_id: this.editingLinie.linie_id,
          data: this.editingLinie.data_inregistrare,
          suma: this.editingLinie.suma_ron,
          explicatie: this.editingLinie.explicatie,
          cont_debit: cont_debit_full,
          cont_credit: cont_credit_full,
        }

        const res = await axios.post(
          '/api/administrare/work/rj/linie/update',
          payload
        )

        const linieActualizata = res.data?.linie || null

        if (linieActualizata) {
          const idx = this.linii.findIndex(
            (l) => l.linie_id === this.editingLinie.linie_id
          )
          if (idx !== -1) {
            this.linii.splice(idx, 1, {
              ...this.linii[idx],
              ...linieActualizata,
            })
          }
        }

        const okMsg =
          res.data?.message || 'Linia a fost actualizată cu succes.'
        if (window.Swal) {
          window.Swal.fire({
            icon: 'success',
            title: 'Succes',
            text: okMsg,
            timer: 2000,
            showConfirmButton: false,
          })
        } else if (window.toastr) {
          window.toastr.success(okMsg)
        }

        // reinițializăm DataTables pentru a reflecta modificările
        this.$nextTick(() => {
          if (this.dtRegistru && this.dtRegistru.destroy) {
            this.dtRegistru.destroy()
            this.dtRegistru = null
          }
          this.$nextTick(() => {
            this.initDataTable()
          })
        })

        // închidem modalul prin helper-ul global, dacă există
        if (typeof this.modal === 'function') {
          this.modal('modalEditLinieRJ').close()
        }
      } catch (e) {
        console.error('Eroare la actualizarea liniei Registru Jurnal:', e)
        const msg =
          e?.response?.data?.error ||
          'A apărut o eroare la actualizarea liniei.'
        if (window.Swal) {
          window.Swal.fire({
            icon: 'error',
            title: 'Eroare',
            text: msg,
          })
        } else if (window.toastr) {
          window.toastr.error(msg)
        }
      }

      this.savingEdit = false
    },
    async fetchTipuriDocumente() {
      try {
        const res = await axios.get('/api/administrare/work/utils/tipuri-documente')
        this.tipuriDocument = res.data || []
      } catch (e) {
        console.error('Nu s-au putut incarca tipurile de documente', e)
      }
    },
    async showDetalii(linie) {
      if (!linie || !linie.reg_jurnal_id) return

      this.detaliiLoading = true
      this.detaliiDoc = { antet: null, linii: [], repartizari: [] }

      // Open modal manually since we're using jQuery/Bootstrap 5 mix
      try {
        const $m = window.$('#modalDetaliiDocument')
        if ($m.length) {
          const bootstrapModal = new window.bootstrap.Modal($m[0])
          bootstrapModal.show()
        }
      } catch (e) { console.warn('Bootstrap modal init error', e) }

      try {
        const res = await axios.get('/api/administrare/work/rj/document', {
          params: {
            firma_id: this.firmaId,
            reg_jurnal_id: linie.reg_jurnal_id
          }
        })
        this.detaliiDoc = res.data
      } catch (e) {
        console.error('Failed to fetch document details', e)
        if (window.Swal) {
          window.Swal.fire({ icon: 'error', title: 'Eroare', text: 'Nu s-au putut încărca detaliile documentului.' })
        }
      } finally {
        this.detaliiLoading = false
      }
    },
    isInvoice(tip) {
      if (!tip) return false
      const t = tip.toLowerCase()
      return t.includes('factura_iesire') || t.includes('factura iesire') ||
        t.includes('factura_intrare') || t.includes('factura intrare')
    },
    shouldShowRepartizariAlert(tip) {
      if (!tip) return false
      const t = tip.toLowerCase()
      const list = ['chitanta', 'ordin plata', 'ordin_plata', 'dispozitie', 'factura']
      return list.some(item => t.includes(item))
    },
    openNotaContabilaModal() {
      if (this.$refs.notaModal) {
        this.$refs.notaModal.showModal();
      }
    },
    confirmDelete(linie) {
      if (!linie || !linie.reg_jurnal_id) return;

      const msg = `Ești pe cale să ștergi definitiv documentul <strong>${(linie.doc_serie || '') + (linie.doc_numar || '')}</strong> (${linie.tip_document}). <br><br> Această acțiune va șterge documentul din toate modulele (Facturi, Plăți, etc.) și din Registru Jurnal. <br><br> <span class="text-danger fw-bold">Acțiunea este irevocabilă!</span>`;

      Swal.fire({
        title: 'Confirmă ștergerea definitivă',
        html: msg,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Da, șterge definitiv!',
        cancelButtonText: 'Anulează',
        reverseButtons: true
      }).then((result) => {
        if (result.isConfirmed) {
          this.deleteLinie(linie.reg_jurnal_id);
        }
      });
    },
    async deleteLinie(rjId) {
      this.loading = true;
      try {
        const res = await axios.delete(`/api/administrare/work/rj/delete/${rjId}`, {
          params: { firma_id: this.firmaId }
        });

        Swal.fire({
          icon: 'success',
          title: 'Șters!',
          text: res.data.message || 'Documentul a fost șters cu succes.',
          timer: 2000,
          showConfirmButton: false
        });

        // Reîncărcăm lista pentru a reflecta ștergerea
        this.loadRegistru();
      } catch (e) {
        console.error('Eroare la ștergerea documentului:', e);
        const errorMsg = e.response?.data?.error || 'A apărut o eroare la ștergerea documentului.';
        Swal.fire({
          icon: 'error',
          title: 'Eroare',
          text: errorMsg
        });
        this.loading = false;
      }
    }
  },
}
</script>

<style scoped>
#tabel_registru_jurnal {
  width: 100% !important;
}

.clickable-cell {
  cursor: pointer;
  transition: background-color 0.2s;
}

.clickable-cell:hover {
  background-color: rgba(0, 158, 247, 0.05) !important;
}

.bg-light-success {
  background-color: #e8fff3 !important;
}
</style>